import { RefObject } from 'react'

import { Cell, Header } from '@tanstack/react-table'
import isNil from 'lodash.isnil'
import { css } from 'styled-components'

import { getValueOfTailwindVariable } from '../../utils'

import { TableCellStickiness, TableDropTargetDragState, TableRowDragState } from './types'

export const isTableRowDragState = <TData>(data: unknown): data is TableRowDragState<TData> => {
  if (isNil(data)) {
    return false
  }
  const dragState = data as TableRowDragState<TData>
  return (
    !!dragState.row &&
    !!dragState.row.id &&
    !isNil(dragState.row.index) &&
    !!dragState.row.original &&
    !!dragState.tableUuid
  )
}

export const isTableDropTargetDragState = (data: unknown): data is TableDropTargetDragState => {
  if (isNil(data)) {
    return false
  }
  const dragState = data as TableDropTargetDragState
  return dragState.isTableDropTarget === true && !!dragState.tableUuid
}

export type TableCellStickyStylesProps = {
  $left: number
  $right: number
  $isScrolledToLeft: boolean
  $isScrolledToRight: boolean
  $stickiness: TableCellStickiness
}
export const TableCellStickyStyles = css<TableCellStickyStylesProps>`
  ${({ $left, $right, $isScrolledToLeft, $isScrolledToRight, $stickiness }) => `
    ${
      $stickiness !== 'none'
        ? `
           position: sticky;
           z-index: 1;
          `
        : `position: relative;`
    };
    ${$stickiness === 'left' ? `left: ${$left}px` : $stickiness === 'right' ? `right: ${$right}px` : ''};
    &::after {
      content: '';
      position: absolute;
      top: 0;
      height: 100%;
      width: 8px;
      opacity: 0;
      transition: opacity 0.2s ease-in-out;

      ${
        $stickiness === 'right' && !$isScrolledToRight
          ? `
          left: -8px;
          background: linear-gradient(to right, transparent, ${getValueOfTailwindVariable('--color-neutral-150')});
          opacity: 1;
      `
          : ''
      };
      ${
        $stickiness === 'left' && !$isScrolledToLeft
          ? `
          right: -8px;
          background: linear-gradient(to left, transparent, ${getValueOfTailwindVariable('--color-neutral-150')});
          opacity: 1;
      `
          : ''
      };
    }
  `}
`

export const getStickyColumnProperties = <TData = unknown>(
  cell: Cell<TData, unknown> | Header<TData, unknown>,
  index: number,
  arr: Cell<TData, unknown>[] | Header<TData, unknown>[],
  headersByColumnKeyRef: RefObject<Map<string, HTMLTableCellElement>>
) => {
  let stickiness: TableCellStickiness = 'none'

  let left = 0
  if (cell.column.columnDef.meta?.stickiness === 'left') {
    stickiness = 'left'
    for (let i = 0; i < index; i++) {
      const otherColumn = arr[i]?.column
      if (otherColumn) {
        if (otherColumn.columnDef.meta?.stickiness !== 'left') {
          stickiness = 'none'
          break
        }
        const otherColumnId = otherColumn.id
        left +=
          headersByColumnKeyRef.current?.get(otherColumnId)?.getBoundingClientRect().width ?? 0
      }
    }
  }
  const isLastLeftStickyColumn =
    stickiness === 'left' &&
    cell.column.columnDef.meta?.stickiness === 'left' &&
    index < arr.length - 1 &&
    arr[index + 1]?.column.columnDef.meta?.stickiness !== 'left'

  let right = 0
  if (cell.column.columnDef.meta?.stickiness === 'right') {
    stickiness = 'right'
    for (let i = arr.length - 1; i > index; i--) {
      const otherColumn = arr[i]?.column
      if (otherColumn) {
        if (otherColumn.columnDef.meta?.stickiness !== 'right') {
          stickiness = 'none'
          break
        }
        const otherColumnId = otherColumn.id
        right +=
          headersByColumnKeyRef.current?.get(otherColumnId)?.getBoundingClientRect().width ?? 0
      }
    }
  }
  const isFirstRightStickyColumn =
    stickiness === 'right' &&
    cell.column.columnDef.meta?.stickiness === 'right' &&
    index > 0 &&
    arr[index - 1]?.column.columnDef.meta?.stickiness !== 'right'

  return {
    isLastLeftStickyColumn,
    isFirstRightStickyColumn,
    left,
    right,
    stickiness,
  }
}
